import { useEffect, useState } from 'react';

import { LANGS, LANG_CODES, LANG_NAMES, type Deck, type Lang, type UserSettings } from '@lexio/shared';

import { BuildStamp } from '../components/BuildStamp';
import { DirectionPicker } from '../components/DirectionPicker';
import { getSettings, listDecks, listLangs, logout, patchSettings, setDeckActive } from '../lib/api';

interface Props {
  onLoggedOut: () => void;
  onSettingsChanged: (s: UserSettings) => void;
}

export function SettingsPage({ onLoggedOut, onSettingsChanged }: Props) {
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [langCounts, setLangCounts] = useState<Partial<Record<Lang, number>>>({});
  const [pairCounts, setPairCounts] = useState<Record<string, number>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void getSettings().then((r) => setSettings(r.settings));
    void listDecks().then((r) => setDecks(r.decks));
    void listLangs().then((r) => {
      setLangCounts(r.counts);
      setPairCounts(Object.fromEntries(r.pairs.map((p) => [p.direction, p.conceptCount])));
    });
  }, []);

  const update = async (patch: Partial<UserSettings>) => {
    setSettings((s) => (s ? { ...s, ...patch } : s));
    const res = await patchSettings(patch);
    setSettings(res.settings);
    onSettingsChanged(res.settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const toggleDeck = async (slug: string, active: boolean) => {
    setDecks((prev) => prev.map((d) => (d.slug === slug ? { ...d, active } : d)));
    await setDeckActive(slug, active);
  };

  const toggleShowLang = (lang: Lang) => {
    if (!settings) return;
    const next = settings.showLangs.includes(lang)
      ? settings.showLangs.filter((l) => l !== lang)
      : [...settings.showLangs, lang];
    void update({ showLangs: next });
  };

  if (!settings) return <div className="center muted">Načítám…</div>;

  return (
    <div className="settings">
      <section>
        <h3>Směry zkoušení</h3>
        <p className="muted small">
          Každý směr se učí zvlášť — poznat slovo a vybavit si ho jsou dvě různé dovednosti.
          Šipkou <strong>↔</strong> směr otočíš.
        </p>
        <DirectionPicker
          value={settings.directions}
          onChange={(directions) => void update({ directions })}
          pairCounts={pairCounts}
        />
      </section>

      <section>
        <h3>Ukázat i další jazyky</h3>
        <p className="muted small">
          Po odkrytí karty uvidíš stejný výraz i v těchto jazycích — vedle sebe, pro srovnání.
        </p>
        <div className="chips">
          {LANGS.map((l) => (
            <button
              key={l}
              className={`chip-btn ${settings.showLangs.includes(l) ? 'active' : ''}`}
              onClick={() => toggleShowLang(l)}
              disabled={!langCounts[l]}
              title={langCounts[l] ? `${langCounts[l]} slov` : 'Zatím žádný obsah'}
            >
              {LANG_CODES[l]}
              <span className="chip-count">{langCounts[l] ?? 0}</span>
            </button>
          ))}
        </div>
      </section>

      <section>
        <h3>Denní dávka</h3>

        <label className="field">
          <span>
            Nových slovíček za den: <strong>{settings.newPerDay}</strong>
          </span>
          <input
            type="range"
            min={0}
            max={50}
            value={settings.newPerDay}
            onChange={(e) => void update({ newPerDay: Number(e.target.value) })}
          />
          <small className="muted">
            Platí dohromady pro všechny směry. Opakování se přidávají navrch.
          </small>
        </label>

        <label className="field">
          <span>
            Denní cíl (karet): <strong>{settings.dailyGoal}</strong>
          </span>
          <input
            type="range"
            min={5}
            max={200}
            step={5}
            value={settings.dailyGoal}
            onChange={(e) => void update({ dailyGoal: Number(e.target.value) })}
          />
          <small className="muted">Podle toho se počítá série splněných dní.</small>
        </label>
      </section>

      <section>
        <h3>Náročnost opakování</h3>
        <label className="field">
          <span>
            Cílová úspěšnost: <strong>{Math.round(settings.requestRetention * 100)} %</strong>
          </span>
          <input
            type="range"
            min={0.7}
            max={0.98}
            step={0.01}
            value={settings.requestRetention}
            onChange={(e) => void update({ requestRetention: Number(e.target.value) })}
          />
          <small className="muted">
            Vyšší hodnota = častější opakování a méně zapomínání, ale víc práce každý den.
            90 % je rozumný výchozí kompromis.
          </small>
        </label>
      </section>

      <section>
        <h3>Balíčky</h3>
        <p className="muted small">Vypnutý balíček přestane dodávat nová slovíčka. Už rozučené karty zůstávají.</p>
        <ul className="deck-list">
          {decks.map((d) => (
            <li key={d.slug}>
              <label>
                <input
                  type="checkbox"
                  checked={d.active}
                  onChange={(e) => void toggleDeck(d.slug, e.target.checked)}
                />
                <span className="deck-name">{d.name}</span>
                <span className="muted small">{d.conceptCount}</span>
              </label>
              <div className="deck-langs">
                {LANGS.filter((l) => d.langCoverage[l]).map((l) => (
                  <span className="lang-pill" key={l} title={`${LANG_NAMES[l]}: ${d.langCoverage[l]}`}>
                    {LANG_CODES[l]}
                  </span>
                ))}
              </div>
              {d.description && <p className="muted small deck-desc">{d.description}</p>}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h3>Nasazení</h3>
        <p className="muted small">
          Číslo verze a git hash aktuálního buildu na serveru. Po aktualizaci
          PWA na telefonu sem koukni, jestli už běží nová verze.
        </p>
        <BuildStamp />
      </section>

      <section>
        <button
          className="btn ghost"
          onClick={async () => {
            await logout();
            onLoggedOut();
          }}
        >
          Odhlásit se
        </button>
      </section>

      {saved && <div className="toast">Uloženo</div>}
    </div>
  );
}
